import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import deviceService from '../lib/device.service';
import type {
  Device, DeviceFilter, DeviceLogFilter, CreateCommandRequest, EnableLiveLoggingRequest, EnableMemoryMonitorRequest,
  RebootRequest, ShellCommandRequest,
} from '../types';

// Query keys
export const deviceKeys = {
  all: ['devices'] as const,
  lists: () => [...deviceKeys.all, 'list'] as const,
  list: (filter?: DeviceFilter) => [...deviceKeys.lists(), filter] as const,
  // Prefix + per-filter variant, mirroring lists()/list(). Existing
  // invalidateQueries({ queryKey: filterOptions() }) calls still prefix-match
  // every narrowed variant.
  filterOptions: () => [...deviceKeys.all, 'filterOptions'] as const,
  filterOptionsFor: (filter?: DeviceFilter) =>
    [...deviceKeys.filterOptions(), filter] as const,
  breakdown: (rows: string, cols: string, filter?: DeviceFilter) =>
    [...deviceKeys.all, 'breakdown', rows, cols, filter] as const,
  stats: () => [...deviceKeys.all, 'stats'] as const,
  statsFor: (filter?: DeviceFilter) => [...deviceKeys.stats(), filter] as const,
  details: () => [...deviceKeys.all, 'detail'] as const,
  detail: (id: string) => [...deviceKeys.details(), id] as const,
  commands: (id: string) => [...deviceKeys.detail(id), 'commands'] as const,
  logs: (id: string, filter?: DeviceLogFilter) => [...deviceKeys.detail(id), 'logs', filter] as const,
  softwareVersions: (id: string) => [...deviceKeys.detail(id), 'software-versions'] as const,
};

// Unified software inventory for the Device Overview "Software Versions"
// section. Backend snapshot only — no refresh/polling (backend refreshes it
// after install-complete / reboot install-success events).
export function useDeviceSoftwareVersions(deviceId: string) {
  return useQuery({
    queryKey: deviceKeys.softwareVersions(deviceId),
    queryFn: () => deviceService.getSoftwareVersions(deviceId),
    enabled: !!deviceId,
    staleTime: 30_000,
  });
}

// List devices hook
/**
 * `enabled` exists so a caller can hold the first fetch until it knows which
 * sort to ask for. The devices page uses it to wait (briefly) on the favorites
 * query: without the gate the list fires with the default order, favorites
 * arrive, and every row jumps.
 */
export function useDevices(filter?: DeviceFilter, enabled = true) {
  return useQuery({
    queryKey: deviceKeys.list(filter),
    queryFn: () => deviceService.list(filter),
    enabled,
    placeholderData: keepPreviousData,
    // Refresh on mount (back-to-list gives fresh data, not 5 min cache) and
    // poll every 60 s so online/offline transitions and new enrollments show
    // up without user action. Polling is silent — caller should distinguish
    // initial-mount fetch from background refetch when deciding whether to
    // render a skeleton.
    refetchOnMount: 'always',
    refetchInterval: 60_000,
  });
}

// Device filter options hook (distinct types and OS versions)
/**
 * Distinct filter values still reachable under `filter`. Omit `filter` for the
 * tenant-wide list (what the tag editor wants).
 *
 * `keepPreviousData` matters here: without it the facet menu would empty out
 * for a moment every time the user narrows, which reads as "no options" right
 * as they're trying to pick one.
 */
export function useDeviceFilterOptions(filter?: DeviceFilter) {
  return useQuery({
    queryKey: deviceKeys.filterOptionsFor(filter),
    queryFn: () => deviceService.getFilterOptions(filter),
    staleTime: 5 * 60 * 1000, // Consider fresh for 5 minutes
    placeholderData: keepPreviousData,
  });
}

// Set device ownership (internal vs customer, super-admin-only). Writes the
// returned device into the detail cache and invalidates the list so the
// marker + ownership filter reflect the change immediately.
export function useSetDeviceInternal(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (isInternal: boolean) => deviceService.setInternal(deviceId, isInternal),
    onSuccess: ({ is_internal }) => {
      // Backend echoes only { id, is_internal } — patch the cached device in
      // place rather than overwriting it with the partial response.
      queryClient.setQueryData<Device | undefined>(deviceKeys.detail(deviceId), (prev) =>
        prev ? { ...prev, is_internal } : prev,
      );
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
}

/** Device counts across two dimensions — one query, not one per row value. */
export function useDeviceBreakdown(rows: string, cols: string, filter?: DeviceFilter) {
  return useQuery({
    queryKey: deviceKeys.breakdown(rows, cols, filter),
    queryFn: () => deviceService.getBreakdown(rows, cols, filter),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

// Device fleet stats hook (total, online, offline, pending, blocked)
export function useDeviceStats(filter?: DeviceFilter, refetchInterval?: number) {
  return useQuery({
    queryKey: deviceKeys.statsFor(filter),
    queryFn: () => deviceService.getStats(filter),
    placeholderData: keepPreviousData,
    refetchInterval,
    // Always refresh the fleet counters on mount — same reasoning as
    // useDevices. Keeps the tab chips (Online N / Offline N) aligned
    // with the list rows after navigating back from detail.
    refetchOnMount: 'always',
  });
}

// Get single device hook
export function useDevice(id: string) {
  return useQuery({
    queryKey: deviceKeys.detail(id),
    queryFn: () => deviceService.get(id),
    enabled: !!id,
    // Keep the detail in sync with fleet state without user action.
    // Covers the post-reboot case QC reported where the device briefly
    // goes offline, comes back, but the chip stays Offline until F5
    // because there is no push-update path into this specific query.
    refetchOnMount: 'always',
    refetchInterval: 30_000,
  });
}

// Get device commands hook
export function useDeviceCommands(deviceId: string) {
  return useQuery({
    queryKey: deviceKeys.commands(deviceId),
    queryFn: () => deviceService.getCommands(deviceId),
    enabled: !!deviceId,
  });
}

// Delete device mutation
export function useDeleteDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deviceService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: deviceKeys.stats() });
    },
  });
}

// Send command mutation
export function useSendCommand(deviceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (command: CreateCommandRequest) =>
      deviceService.sendCommand(deviceId, command),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.commands(deviceId) });
    },
  });
}

// Get device logs hook. `enabled` is an explicit opt-in so the REST
// /logs?limit=100 request doesn't fire on every page mount — most
// device-detail tabs don't need it. Callers gate on the actual tab
// being visible (e.g. Monitoring → Logs sub-tab).
export function useDeviceLogs(deviceId: string, filter?: DeviceLogFilter, enabled = true) {
  return useQuery({
    queryKey: deviceKeys.logs(deviceId, filter),
    queryFn: () => deviceService.getLogs(deviceId, filter),
    enabled: !!deviceId && enabled,
    placeholderData: keepPreviousData, // Keep old data while fetching new
    staleTime: 30 * 1000, // Consider data fresh for 30 seconds
  });
}

// Enable live logging mutation
export function useEnableLiveLogging(deviceId: string) {
  return useMutation({
    mutationFn: (options?: EnableLiveLoggingRequest) =>
      deviceService.enableLiveLogging(deviceId, options),
  });
}

// Disable live logging mutation
export function useDisableLiveLogging(deviceId: string) {
  return useMutation({
    mutationFn: (reason?: string) => deviceService.disableLiveLogging(deviceId, reason),
  });
}

// Enable activity monitor mutation
export function useEnableActivityMonitor(deviceId: string) {
  return useMutation({
    mutationFn: () => deviceService.enableActivityMonitor(deviceId),
  });
}

// Disable activity monitor mutation
export function useDisableActivityMonitor(deviceId: string) {
  return useMutation({
    mutationFn: () => deviceService.disableActivityMonitor(deviceId),
  });
}

// Enable memory monitor mutation
export function useEnableMemoryMonitor(deviceId: string) {
  return useMutation({
    mutationFn: (options?: EnableMemoryMonitorRequest) =>
      deviceService.enableMemoryMonitor(deviceId, options),
  });
}

// Disable memory monitor mutation
export function useDisableMemoryMonitor(deviceId: string) {
  return useMutation({
    mutationFn: () => deviceService.disableMemoryMonitor(deviceId),
  });
}

// Memory monitor heartbeat mutation
export function useMemoryMonitorHeartbeat(deviceId: string) {
  return useMutation({
    mutationFn: () => deviceService.memoryMonitorHeartbeat(deviceId),
  });
}

// Factory reset mutation (instant via MDM Protocol, requires online device)
export function useFactoryReset(deviceId: string) {
  return useMutation({
    mutationFn: (needUserAccept: boolean = true) =>
      deviceService.factoryReset(deviceId, needUserAccept),
  });
}

// Get device info mutation (fetch from device via MDM Protocol, requires online device)
export function useGetDeviceInfo(deviceId: string) {
  return useMutation({
    mutationFn: () => deviceService.getDeviceInfo(deviceId),
  });
}

// Diagnostic v1.1 hooks
export function useReboot(deviceId: string) {
  return useMutation({
    mutationFn: (req?: RebootRequest) => deviceService.reboot(deviceId, req),
  });
}

export function useStorageInfo(deviceId: string) {
  return useMutation({
    mutationFn: () => deviceService.getStorageInfo(deviceId),
  });
}

export function useExecuteShellCommand(deviceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: ShellCommandRequest) => deviceService.executeShellCommand(deviceId, req),
    onMutate: () => {
      // Immediately refresh audit list to show "processing" status
      queryClient.invalidateQueries({ queryKey: [...deviceKeys.detail(deviceId), 'actionAudits'] });
    },
    onSettled: () => {
      // Refresh audit list when command completes (success or error)
      queryClient.invalidateQueries({ queryKey: [...deviceKeys.detail(deviceId), 'actionAudits'] });
    },
  });
}

// Unified action-audits hook. action filter is optional; pass
// "shell.execute" from the shell executor to scope itself, omit it
// from the broader Audit tab to see every category.
export function useActionAudits(
  deviceId: string,
  opts: { limit?: number; offset?: number; action?: string; pollWhileActive?: boolean } = {},
) {
  const { limit = 50, offset = 0, action, pollWhileActive = false } = opts;
  return useQuery({
    queryKey: [...deviceKeys.detail(deviceId), 'actionAudits', limit, offset, action ?? ''] as const,
    queryFn: () => deviceService.listActionAudits(deviceId, { limit, offset, action }),
    enabled: !!deviceId,
    // Poll every 2s while a command is executing so the shell executor
    // can show real-time pending → success transitions.
    refetchInterval: pollWhileActive ? 2000 : false,
  });
}
